import { ChevronLeft, ChevronRight, Search } from "lucide-react";
import type { ReactNode } from "react";

import type { PaginatedListState } from "@/lib/pagination";

/**
 * Presentation layer for ``usePaginatedList`` - renders:
 *
 *   - a search input bound to ``state.search`` / ``state.setSearch``
 *   - optional extra header controls (filters, etc.)
 *   - the caller-supplied ``children`` (table, grid, whatever)
 *   - a footer with Prev / Next buttons + "Page N of M · 1234 total"
 *
 * Keeping this decoupled from ``usePaginatedList`` means a page can
 * call the hook alone (when it wants custom layout) OR pair the hook
 * with this component for the default presentation. Most pages use
 * the default - consistency across screens was one of the goals.
 *
 * The search debounce + pageIndex reset live inside the hook; this
 * component is purely render.
 */
export function PaginatedList<TItem>(props: {
  state: PaginatedListState<TItem>;
  searchPlaceholder?: string;
  /** Rendered between search + pagination footer. */
  children: ReactNode;
  /** Rendered inline next to the search input. */
  filters?: ReactNode;
  /** Label shown in the footer alongside the page indicator. */
  itemNoun?: string;
  /** Render a custom empty state when there are zero rows total. */
  emptyState?: ReactNode;
}): JSX.Element {
  const { state } = props;
  const showingStart = state.total === 0 ? 0 : state.offset + 1;
  const showingEnd = state.offset + state.items.length;
  const itemNoun = props.itemNoun ?? "items";
  const isLoading = state.query.isLoading;
  const isFetching = state.query.isFetching;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <label className="relative flex-1 sm:max-w-sm">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={state.search}
            onChange={(e) => state.setSearch(e.target.value)}
            placeholder={props.searchPlaceholder ?? `Search ${itemNoun}…`}
            aria-label={props.searchPlaceholder ?? `Search ${itemNoun}`}
            className="w-full rounded-md border bg-background py-1.5 pl-8 pr-3 text-sm"
          />
        </label>
        {props.filters}
        {isFetching && !isLoading && (
          <span className="text-xs text-muted-foreground" aria-live="polite">
            Updating…
          </span>
        )}
      </div>

      {/* Three-way split on the body:
          - Initial load (no data yet) → render a loading skeleton,
            suppressing both children (empty) and the empty-state
            (would lie about having zero rows).
          - Zero total AND not currently loading AND no search string
            → empty-state.
          - Otherwise → children (table, cards, whatever).
          The combination eliminates the "No X found" → populate flash
          that happened when the footer or empty-state rendered off
          the initial empty ``state.items``. */}
      {isLoading ? (
        <p
          className="rounded-lg border border-dashed bg-muted/20 p-5 text-center text-sm text-muted-foreground"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          Loading {itemNoun}…
        </p>
      ) : state.total === 0 && props.emptyState && state.search === "" ? (
        props.emptyState
      ) : (
        props.children
      )}

      <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-3 text-xs text-muted-foreground">
        <span>
          {isLoading
            ? `Loading ${itemNoun}…`
            : state.total === 0
              ? `No ${itemNoun} found`
              : `Showing ${showingStart}–${showingEnd} of ${state.total} ${itemNoun}`}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => state.setPageIndex(state.pageIndex - 1)}
            disabled={!state.hasPrev || isLoading}
            className="flex items-center gap-1 rounded-md border bg-background px-2 py-1 hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ChevronLeft className="h-3 w-3" /> Prev
          </button>
          <span>{isLoading ? "Page …" : `Page ${state.pageIndex + 1} of ${state.pageCount}`}</span>
          <button
            type="button"
            onClick={() => state.setPageIndex(state.pageIndex + 1)}
            disabled={!state.hasNext || isLoading}
            className="flex items-center gap-1 rounded-md border bg-background px-2 py-1 hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40"
          >
            Next <ChevronRight className="h-3 w-3" />
          </button>
        </div>
      </div>
    </div>
  );
}
